import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import {
  CollectionItem,
  CollectionFile,
  CaptchaData,
  CreateUploadUrlRes,
} from '../types';

// Supabase & Worker Config
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const WORKER_URL = import.meta.env.VITE_WORKER_URL || '';
const R2_PUBLIC_DOMAIN = import.meta.env.VITE_R2_PUBLIC_DOMAIN || '';

export interface RawFileToUpload {
  name: string; // e.g. "VietPhrase.txt"
  type: CollectionFile['type'];
  content: string; // text content
}

export const collectionService = {
  /**
   * Fetches paginated collection list from Supabase PostgREST (anon key)
   */
  async fetchCollections(page = 1, limit = 20): Promise<{ data: CollectionItem[]; total: number }> {
    const offset = (page - 1) * limit;

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Chưa cấu hình biến môi trường VITE_SUPABASE_URL hoặc VITE_SUPABASE_ANON_KEY.');
    }

    // Try fetching from collections table first (contains secretKeyHash for owner matching), fallback to public_collections
    let endpoint = `${SUPABASE_URL}/rest/v1/collections?select=*&order=createdAt.desc&limit=${limit}&offset=${offset}`;
    let response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        Prefer: 'count=exact',
      },
    });

    if (!response.ok) {
      endpoint = `${SUPABASE_URL}/rest/v1/public_collections?select=*&order=createdAt.desc&limit=${limit}&offset=${offset}`;
      response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          Prefer: 'count=exact',
        },
      });
    }

    if (!response.ok) {
      throw new Error(`Lỗi tải danh sách bộ sưu tập (${response.status})`);
    }

    const data: CollectionItem[] = await response.json();
    const contentRange = response.headers.get('content-range');
    let total = data.length;
    if (contentRange) {
      const parts = contentRange.split('/');
      if (parts[1]) {
        total = parseInt(parts[1], 10);
      }
    }

    return { data, total };
  },

  /**
   * Fetches single collection details by ID or shortCode
   */
  async fetchCollectionById(idOrShortCode: string): Promise<CollectionItem | null> {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Chưa cấu hình biến môi trường VITE_SUPABASE_URL hoặc VITE_SUPABASE_ANON_KEY.');
    }

    const isUuid = idOrShortCode.length === 36 && idOrShortCode.includes('-');
    const queryFilter = isUuid
      ? `id=eq.${idOrShortCode}`
      : `"shortCode"=eq.${idOrShortCode}`;

    let endpoint = `${SUPABASE_URL}/rest/v1/collections?${queryFilter}&select=*`;
    let response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
    });

    if (!response.ok) {
      endpoint = `${SUPABASE_URL}/rest/v1/public_collections?${queryFilter}&select=*`;
      response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
    }

    if (!response.ok) {
      throw new Error(`Lỗi lấy thông tin bộ sưu tập (${response.status})`);
    }

    const data: CollectionItem[] = await response.json();
    return data[0] || null;
  },

  /**
   * Generates CAPTCHA from Worker
   */
  async generateCaptcha(): Promise<CaptchaData> {
    if (!WORKER_URL) {
      throw new Error('Chưa cấu hình biến môi trường VITE_WORKER_URL.');
    }

    const response = await fetch(`${WORKER_URL}/api/captcha/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Không thể tạo CAPTCHA');
    }

    return await response.json();
  },

  /**
   * Upload collection flow: Zip in RAM -> Presigned URL -> PUT R2 -> Finalize
   */
  async uploadCollection(params: {
    name: string;
    author?: string;
    secretKeyHash: string;
    captcha: { nonce: string; questionId: string; answer: string };
    rawFiles: RawFileToUpload[];
    onProgress?: (msg: string) => void;
  }): Promise<{ collectionId: string; author: string }> {
    const { name, author, secretKeyHash, captcha, rawFiles, onProgress } = params;

    onProgress?.('Đang nuốt RAM, chờ xíu...');

    // 1. Prepare zip in memory using fflate
    const zipDataMap: Record<string, Uint8Array> = {};
    const fileMetadataList: CollectionFile[] = [];

    for (const file of rawFiles) {
      const u8 = strToU8(file.content);
      zipDataMap[file.name] = u8;

      // Count lines
      const lineCount = file.content.split('\n').filter(l => l.trim().length > 0).length;
      fileMetadataList.push({
        filename: file.name,
        type: file.type,
        size: u8.length,
        wordCount: lineCount,
      });
    }

    const zippedUint8 = zipSync(zipDataMap, { level: 6 });
    const zipBlob = new Blob([zippedUint8], { type: 'application/zip' });

    onProgress?.('Đang tải bộ sưu tập của bạn lên...');

    // 2. Request presigned URL from Worker
    const workerEndpoint = WORKER_URL || '';
    const createUrlRes = await fetch(`${workerEndpoint}/api/collections/create-upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        author: author?.trim() || undefined,
        secretKeyHash,
        captcha,
        files: fileMetadataList,
        zipSize: zipBlob.size,
      }),
    });

    const createData = await createUrlRes.json();
    if (!createUrlRes.ok) {
      throw new Error(createData?.error?.message || 'Lỗi khi yêu cầu tạo URL tải lên.');
    }

    const { collectionId, uploadUrl, author: resAuthor } = createData as CreateUploadUrlRes;

    onProgress?.('Đang tải file lên, sắp xong rồi, chờ tí...');

    // 3. Direct PUT to R2 presigned URL
    if (uploadUrl.includes('/api/mock-upload/')) {
      console.warn('Mock environment: Simulating R2 upload');
    } else {
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/zip' },
        body: zipBlob,
      });

      if (!putRes.ok) {
        throw new Error('Lỗi khi tải file zip lên R2 Storage.');
      }
    }

    onProgress?.('Đang hoàn tất lưu dữ liệu...');

    // 4. Finalize via Worker
    const finalizeRes = await fetch(`${workerEndpoint}/api/collections/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        collectionId,
        secretKeyHash,
        name,
        author: resAuthor,
        files: fileMetadataList,
      }),
    });

    const finalizeData = await finalizeRes.json();
    if (!finalizeRes.ok) {
      throw new Error(finalizeData?.error?.message || 'Lỗi khi hoàn tất bộ sưu tập.');
    }

    return { collectionId, author: resAuthor };
  },

  /**
   * Delete collection via Worker
   */
  async deleteCollection(params: {
    collectionId: string;
    secretKeyHash: string;
    captcha: { nonce: string; questionId: string; answer: string };
  }): Promise<void> {
    const response = await fetch(`${WORKER_URL}/api/collections/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error?.message || 'Không thể xóa bộ sưu tập.');
    }
  },

  /**
   * Updates author display name for all collections owned by this user (by matching secretKeyHash)
   */
  async updateAuthorName(params: {
    secretKeyHash: string;
    author: string;
  }): Promise<{ updatedCount: number }> {
    const response = await fetch(`${WORKER_URL}/api/collections/update-author`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error?.message || 'Không thể cập nhật tên tác giả.');
    }

    return { updatedCount: data.updatedCount || 0 };
  },

  /**
   * Downloads a single .txt file from the zip stored on R2, unzipping on RAM
   */
  async downloadSingleFile(zipUrlPath: string, filename: string): Promise<void> {
    const cacheBuster = `?t=${Date.now()}`;
    const baseUrl = R2_PUBLIC_DOMAIN
      ? `${R2_PUBLIC_DOMAIN}/${zipUrlPath}`
      : `${SUPABASE_URL || ''}/storage/v1/object/public/collections/${zipUrlPath}`;
    const fullZipUrl = baseUrl + cacheBuster;

    const res = await fetch(fullZipUrl, { cache: 'no-cache' });
    if (!res.ok) {
      throw new Error('Không thể tải file zip từ server lưu trữ');
    }

    const arrayBuffer = await res.arrayBuffer();
    const unzipped = unzipSync(new Uint8Array(arrayBuffer));

    // 1. Try exact key match first
    let targetBytes = unzipped[filename];
    let actualFilename = filename;

    // 2. Fallback: Case-insensitive & path-insensitive lookup
    if (!targetBytes) {
      const cleanTarget = filename.trim().toLowerCase();
      const foundKey = Object.keys(unzipped).find((k) => {
        const cleanKey = k.trim().toLowerCase();
        const baseKey = cleanKey.split('/').pop() || cleanKey;
        return cleanKey === cleanTarget || baseKey === cleanTarget;
      });

      if (foundKey) {
        targetBytes = unzipped[foundKey];
        actualFilename = foundKey.split('/').pop() || filename;
      }
    }

    if (!targetBytes) {
      throw new Error(`File ${filename} không tồn tại trong gói zip.`);
    }

    const textContent = strFromU8(targetBytes);
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = actualFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  /**
   * Downloads full zip and extracts all text files into memory for editing
   */
  async fetchZipFileContents(zipUrlPath: string): Promise<RawFileToUpload[]> {
    const cacheBuster = `?t=${Date.now()}`;
    const baseUrl = R2_PUBLIC_DOMAIN
      ? `${R2_PUBLIC_DOMAIN}/${zipUrlPath}`
      : `${SUPABASE_URL || ''}/storage/v1/object/public/collections/${zipUrlPath}`;
    const fullZipUrl = baseUrl + cacheBuster;

    const res = await fetch(fullZipUrl, { cache: 'no-cache' });
    if (!res.ok) {
      throw new Error('Không thể tải file zip để xem nội dung.');
    }

    const arrayBuffer = await res.arrayBuffer();
    const unzipped = unzipSync(new Uint8Array(arrayBuffer));
    const result: RawFileToUpload[] = [];

    for (const [filename, bytes] of Object.entries(unzipped)) {
      if (filename.endsWith('.txt') || !filename.includes('.')) {
        const content = strFromU8(bytes);
        let type: CollectionFile['type'] = 'VietPhrase';
        const lower = filename.toLowerCase();
        if (lower.includes('name')) type = 'Name';
        else if (lower.includes('phienam')) type = 'PhienAm';
        else if (lower.includes('pronoun')) type = 'Pronouns';
        else if (lower.includes('rule')) type = 'Rule';

        result.push({
          name: filename,
          type,
          content,
        });
      }
    }

    return result;
  },

  /**
   * Update collection flow: Zip in RAM -> Worker Backup current.zip to previous.zip -> PUT R2 -> Finalize
   */
  async updateCollection(params: {
    collectionId: string;
    name: string;
    author?: string;
    secretKeyHash: string;
    captcha: { nonce: string; questionId: string; answer: string };
    rawFiles: RawFileToUpload[];
    onProgress?: (msg: string) => void;
  }): Promise<CollectionItem> {
    const { collectionId, name, author, secretKeyHash, captcha, rawFiles, onProgress } = params;

    onProgress?.('Đang chuẩn bị file zip cập nhật...');

    // 1. Prepare zip in memory using fflate
    const zipDataMap: Record<string, Uint8Array> = {};
    const fileMetadataList: CollectionFile[] = [];

    for (const file of rawFiles) {
      const u8 = strToU8(file.content);
      zipDataMap[file.name] = u8;

      const lineCount = file.content.split('\n').filter(l => l.trim().length > 0).length;
      fileMetadataList.push({
        filename: file.name,
        type: file.type,
        size: u8.length,
        wordCount: lineCount,
      });
    }

    const zippedUint8 = zipSync(zipDataMap, { level: 6 });
    const zipBlob = new Blob([zippedUint8], { type: 'application/zip' });

    onProgress?.('Đang tạo bản sao lưu phiên bản cũ & lấy URL tải lên mới...');

    // 2. Request presigned URL from Worker (Worker handles backing up current.zip -> previous.zip)
    const workerEndpoint = WORKER_URL || '';
    const createUrlRes = await fetch(`${workerEndpoint}/api/collections/create-update-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        collectionId,
        name,
        author: author?.trim() || undefined,
        secretKeyHash,
        captcha,
        files: fileMetadataList,
        zipSize: zipBlob.size,
      }),
    });

    const createData = await createUrlRes.json();
    if (!createUrlRes.ok) {
      throw new Error(createData?.error?.message || 'Lỗi khi tạo bản cập nhật Bộ Sưu Tập.');
    }

    const { uploadUrl, author: resAuthor } = createData as CreateUploadUrlRes;

    onProgress?.('Đang tải file zip bản cập nhật mới lên server...');

    // 3. Direct PUT to R2 presigned URL
    if (uploadUrl.includes('/api/mock-upload/')) {
      console.warn('Mock environment: Simulating R2 update upload');
    } else {
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/zip' },
        body: zipBlob,
      });

      if (!putRes.ok) {
        throw new Error('Lỗi khi tải bản zip cập nhật mới lên R2 Storage.');
      }
    }

    onProgress?.('Đang hoàn tất cập nhật Bộ Sưu Tập...');

    // 4. Finalize update via Worker
    const finalizeRes = await fetch(`${workerEndpoint}/api/collections/finalize-update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        collectionId,
        secretKeyHash,
        name,
        author: resAuthor,
        files: fileMetadataList,
      }),
    });

    const finalizeData = await finalizeRes.json();
    if (!finalizeRes.ok) {
      throw new Error(finalizeData?.error?.message || 'Lỗi khi hoàn tất cập nhật Bộ Sưu Tập.');
    }

    return finalizeData as CollectionItem;
  },

  /**
   * Rollback collection to previous.zip Server-to-Server via Worker
   */
  async rollbackCollection(params: {
    collectionId: string;
    secretKeyHash: string;
    captcha: { nonce: string; questionId: string; answer: string };
  }): Promise<void> {
    const response = await fetch(`${WORKER_URL}/api/collections/rollback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error?.message || 'Không thể khôi phục về phiên bản trước.');
    }
  },

  /**
   * Downloads the full collection zip file
   */
  async downloadFullZip(zipUrlPath: string, collectionName: string): Promise<void> {
    const cacheBuster = `?t=${Date.now()}`;
    const baseUrl = R2_PUBLIC_DOMAIN
      ? `${R2_PUBLIC_DOMAIN}/${zipUrlPath}`
      : `${SUPABASE_URL || ''}/storage/v1/object/public/collections/${zipUrlPath}`;
    const fullZipUrl = baseUrl + cacheBuster;

    const a = document.createElement('a');
    a.href = fullZipUrl;
    a.download = `${collectionName.replace(/[^a-zA-Z0-9_-]/g, '_')}.zip`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
};

